import { useMemo } from 'react';
import { AlertCircle } from 'lucide-react';
import PlayerAvatar from '../ui/PlayerAvatar';
import Card from '../ui/Card';
import Badge from '../ui/Badge';
import Tooltip from '../ui/Tooltip';
import { calculateConservativeRating } from '../../utils/rating';

const LeaderboardTab = ({
  players,
  user,
  setSelectedPlayer,
  setActiveTab,
  isAdmin = false,
}) => {
  // Memoize sorted players to avoid recalculating on every render
  // Only show players who have played at least one match
  const sortedPlayers = useMemo(() => {
    return [...players]
      .filter(p => (p.wins + p.losses) > 0) // Only players who have played matches
      .map(p => ({
        ...p,
        ts: calculateConservativeRating(p.mu, p.sigma),
        winRate: (p.wins + p.losses) > 0 ? (p.wins / (p.wins + p.losses)) : 0,
      }))
      .sort((a, b) => {
        // Primary sort: Rating (descending)
        if (b.ts !== a.ts) {
          return b.ts - a.ts;
        }
        // Secondary sort: Win Rate (descending)
        if (b.winRate !== a.winRate) {
          return b.winRate - a.winRate;
        }
        // Tertiary sort: Name (alphabetical, ascending)
        return a.name.localeCompare(b.name);
      });
  }, [players]);

  return (
    <div className="space-y-4 animate-in fade-in slide-in-from-bottom-2 duration-300">
      <div className="flex justify-between items-center pb-2">
        <div>
          <h2 className="text-lg font-bold tracking-tight text-zinc-900">
            Leaderboard
          </h2>
          <p className="text-xs text-zinc-400 mt-1">
            Only players with matches are shown
          </p>
        </div>
        {isAdmin && <Badge type="admin">Admin Access</Badge>}
      </div>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-zinc-400 border-b border-zinc-100 bg-zinc-50/50">
                <th className="py-3 px-6 font-bold w-16">Rank</th>
                <th className="py-3 px-6 font-bold">Player</th>
                <th className="py-3 px-6 font-bold text-right">Rating</th>
                <th className="py-3 px-6 font-bold text-right hidden sm:table-cell">
                  Win %
                </th>
              </tr>
            </thead>
            <tbody className="text-sm bg-white">
              {players.length === 0 ? (
                <tr key="no-data">
                  <td colSpan="4" className="p-8 text-center text-zinc-400">
                    No data available
                  </td>
                </tr>
              ) : (
                sortedPlayers.map((player, index) => (
                  <tr
                    key={player.id}
                    onClick={() => {
                      setSelectedPlayer(player);
                      setActiveTab('analytics');
                    }}
                    className="border-b border-zinc-50 last:border-0 hover:bg-zinc-50/80 cursor-pointer transition-colors group"
                  >
                    <td className="py-4 px-6 font-mono text-zinc-400 font-medium">
                      #{index + 1}
                    </td>
                    <td className="py-4 px-6">
                      <div className="flex items-center gap-3">
                        <PlayerAvatar
                          initials={player.initials}
                          size="sm"
                          inverted={index === 0}
                        />
                        <div className="flex items-center gap-2">
                          <div>
                            <div className="font-bold text-zinc-900 group-hover:text-indigo-600 transition-colors">
                              {player.name}
                            </div>
                            {player.authUserId ===
                              (user?.username || 'demo-user') && (
                              <span className="text-[9px] text-zinc-400 font-bold uppercase tracking-wider">
                                You
                              </span>
                            )}
                          </div>
                          {player.sigma > 5.5 && (
                            <Tooltip
                              content={`High uncertainty (σ=${player.sigma.toFixed(1)}). Rating needs more games to stabilize.`}
                              side="right"
                            >
                              <AlertCircle className="w-4 h-4 text-amber-500" />
                            </Tooltip>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="py-4 px-6 text-right font-mono font-bold text-zinc-900">
                      {player.ts.toFixed(2)}
                    </td>
                    <td className="py-4 px-6 text-right text-zinc-500 hidden sm:table-cell">
                      {player.wins + player.losses > 0
                        ? `${Math.round(
                            (player.wins / (player.wins + player.losses)) * 100
                          )}%`
                        : '-'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

export default LeaderboardTab;
